// ---------------------------------------------------------------------------
// Low-level CSV parsing shared by importCsvProject.ts (build a brand-new
// project from a CSV-only folder) and updateProjectFromCsv.ts (merge CSVs
// back into an already-open project) — both read the same file format
// exportCsvProject.ts writes.
// ---------------------------------------------------------------------------
import { DEFAULT_UNIT_OPTIONS, UNITS_HA, UNITS_KM2, UNITS_OTHER } from "./units.ts";
import { NO_MODEL } from "./projectEntities.ts";
import { PARAMETER_DEFINITIONS, STATE_VARIABLE_DEFINITIONS } from "./baseTemplate.ts";
import type { ReadableFolder } from "./fsReadable.ts";
import type { ModelType, ParameterValue, StateVariableValue, UnitOption, Zone } from "./types.ts";

/**
 * Ascending by code, like the original's own `ORDER BY CODIGO_PARAMETRO`/
 * `ORDER BY CODIGO_VARIABLE_ESTADO` — the catalog (baseTemplate.ts) isn't
 * declared in that order for every model (AWBM's parameters/state variables
 * are a real example), so this can't be skipped. Used both by
 * exportCsvProject.ts (writer) and this file's own readers below, which rely
 * on the exact same order to line up CSV columns positionally with these
 * definitions. Moved here from exportCsvProject.ts (deep-review,
 * 2026-08-29) — the reader depending on the writer for a shared ordering
 * utility read backwards; this module (already the shared low-level parsing
 * home for both writer and readers) is the natural place for it.
 */
export function sortedByCode<T>(defs: T[], code: (d: T) => number): T[] {
  return [...defs].sort((a, b) => code(a) - code(b));
}

export async function readCsvText(
  folder: ReadableFolder,
  fileName: string
): Promise<string | null> {
  try {
    const handle = await folder.getFileHandle(fileName);
    const file = await handle.getFile();
    return await file.text();
  } catch {
    return null;
  }
}

/** ";"-separated, no quoting (matches exportCsvProject.ts's own writer) — header row dropped, one string[] per data row. */
export function parseSimpleCsv(text: string): string[][] {
  return text
    .split(/\r\n|\n/)
    .filter((l) => l.length > 0)
    .slice(1)
    .map((line) => line.split(";"));
}

/**
 * Column name -> index, from a CSV's own header row — for a file whose real
 * column SET can vary between sources (Ent_Cuencas.csv: the real desktop
 * app's own export, Form1.vb/WriteArchivoCuencas, never wrote Cod_modelo/
 * Cod_modelo_nieve; exportCsvProject.ts's buildSubBasinsCsv does, since the
 * real calculation engine's lect_cuencas() requires them by name — see
 * CLAUDE.md's engine-source audit). Reading by name instead of position
 * means either shape parses correctly, and a column this app doesn't care
 * about (like the two above) is simply never looked up.
 */
export function csvHeaderIndex(text: string): Map<string, number> {
  const headerLine = text.split(/\r\n|\n/).find((l) => l.length > 0) ?? "";
  return new Map(headerLine.split(";").map((name, i) => [name, i]));
}

export function numberOr(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

function nullableNumber(raw: string | undefined): number | null {
  if (raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Ent_Config.csv only gives the raw coefficient, not which of the 3 known unit rows it came from (nor a custom label) — matched back to km²/ha by value, everything else treated as "Other" with a generic "Hm3" label. */
function unitOptionsFromCoefficient(coefficient: number): UnitOption[] {
  const matchesKnown = (code: number) =>
    Math.abs(coefficient - (DEFAULT_UNIT_OPTIONS.find((o) => o.code === code)?.value ?? NaN)) <
    1e-12;
  if (matchesKnown(UNITS_KM2)) return DEFAULT_UNIT_OPTIONS;
  if (matchesKnown(UNITS_HA))
    return DEFAULT_UNIT_OPTIONS.map((o) => ({ ...o, checked: o.code === UNITS_HA }));
  return DEFAULT_UNIT_OPTIONS.map((o) =>
    o.code === UNITS_OTHER
      ? { ...o, checked: true, units: "Hm3", value: coefficient }
      : { ...o, checked: false }
  );
}

/** Is this an unassigned/missing reference — "0" (this app's own sentinel) or genuinely blank. */
function isUnassignedName(name: string | undefined): boolean {
  return !name || name === "0";
}

/** One model's parameter/state-variable definitions, sorted the same way exportCsvProject.ts writes each `Ent_{modelo}_Param.csv` row — used to match a row's columns back to the right definition positionally (see this file's own header on why POSITION, not name). */
export function zoneParamDefsFor(model: ModelType) {
  return {
    paramDefs: sortedByCode(
      PARAMETER_DEFINITIONS.filter(
        (d) => d.modelTypeCode === model.modelTypeCode && d.kind === model.kind
      ),
      (d) => d.parameterCode
    ),
    stateDefs: sortedByCode(
      STATE_VARIABLE_DEFINITIONS.filter(
        (d) => d.modelTypeCode === model.modelTypeCode && d.kind === model.kind
      ),
      (d) => d.stateVariableCode
    ),
  };
}

/**
 * One `Ent_{modelo}_Param.csv` row's parameter/state-variable values for
 * `zoneCode` — `cols[0]` is the zone name (read separately by the caller),
 * `cols[1..]` are the parameters in `paramDefs`' order followed by the
 * state variables in `stateDefs`' order (see `zoneParamDefsFor`). Shared by
 * importCsvProject.ts (brand-new project) and updateProjectFromCsv.ts
 * (merge into an existing one) — both build the same two value lists from
 * the same row shape, differing only in what they do with a zone that
 * already exists (create-only vs. find-or-create-and-replace).
 */
export function parseZoneParamRow(
  cols: string[],
  zoneCode: number,
  kind: number,
  paramDefs: ReturnType<typeof zoneParamDefsFor>["paramDefs"],
  stateDefs: ReturnType<typeof zoneParamDefsFor>["stateDefs"]
): { parameterValues: ParameterValue[]; stateVariableValues: StateVariableValue[] } {
  return {
    parameterValues: paramDefs.map((d, i) => ({
      zoneCode,
      parameterCode: d.parameterCode,
      kind,
      value: numberOr(cols[1 + i], d.defaultValue ?? 0),
    })),
    stateVariableValues: stateDefs.map((d, i) => ({
      zoneCode,
      stateVariableCode: d.stateVariableCode,
      kind,
      value: numberOr(cols[1 + paramDefs.length + i], d.defaultValue ?? 0),
    })),
  };
}

// ---------------------------------------------------------------------------
// Row parsers below encode Ent_Cuencas/Ent_Apo/Ent_Acu/Ent_Config.csv's
// column layout in exactly one place — importCsvProject.ts (build a
// brand-new project) and updateProjectFromCsv.ts (merge into an existing
// one) each read the same files but do different things with a row (create
// vs. update-or-create, silent vs. notice-worthy unresolved references), so
// they still own that branching themselves; only the "what does this row of
// columns mean" part lives here.
// ---------------------------------------------------------------------------

/**
 * `Codigo` is the real identity: the original app's own UI never exposes a
 * separate code field (it just repeats the one name the user types into
 * both CSV columns on export), but the real calculation engine
 * (`EvalHid_Calculo/Globales.cs`) only ever reads/uses `Codigo` — for
 * outlet points `Nombre` is parsed and never used again, and for aquifers
 * it isn't even read. A CSV producer that writes genuinely different
 * values (unlike the original app) must be treated as `Codigo` being the
 * authoritative label, with `Nombre` only as a fallback if `Codigo` is
 * blank.
 */
export function parseOutletPointName(cols: string[]): string {
  return cols[0] || cols[1] || "";
}

interface AquiferCsvFields {
  name: string;
  vo: number | null;
  alfa: number | null;
  outletCode: number | null;
}

export function parseAquiferCsvFields(
  cols: string[],
  outletCodeByName: Map<string, number>
): AquiferCsvFields {
  const [code, name, vo, alfa, outletRef] = cols;
  return {
    // See parseOutletPointName's comment — Codigo is authoritative, Nombre
    // is an original-app-only cosmetic duplicate the real engine never reads.
    name: code || name || "",
    vo: nullableNumber(vo),
    alfa: nullableNumber(alfa),
    outletCode: outletRef ? (outletCodeByName.get(outletRef) ?? null) : null,
  };
}

interface SubBasinCsvFields {
  name: string;
  areaRaw: string;
  precipAdjustmentRaw: string;
  etpAdjustmentRaw: string;
  tempAdjustmentRaw: string;
  partialResultsRaw: string;
  outletCode: number;
  /** Raw CSV name if it didn't resolve to a real outlet (and wasn't just left unassigned) — null otherwise. */
  outletUnresolvedName: string | null;
  aquiferCode: number;
  aquiferUnresolvedName: string | null;
  zoneCode: number | null;
  zoneUnresolvedName: string | null;
  snowZoneCode: number | null;
  snowZoneUnresolvedName: string | null;
  modelTypeCode: number;
  snowModelTypeCode: number;
}

/**
 * Parses one Ent_Cuencas.csv row, resolving its name references (outlet/
 * aquifer/zone/snow zone) against the maps built from the other CSVs
 * already read. By COLUMN NAME (`columnIndex`, see `csvHeaderIndex`), not
 * position — Cod_modelo/Cod_modelo_nieve, if present, are never looked up:
 * the sub-basin's model is derived from its zone below (same rule as
 * assignZoneInBulk, projectEntities.ts), so a file with or without those 2
 * columns parses the same either way.
 */
export function parseSubBasinCsvFields(
  cols: string[],
  columnIndex: Map<string, number>,
  outletCodeByName: Map<string, number>,
  aquiferCodeByName: Map<string, number>,
  runoffZoneCodeByName: Map<string, number>,
  snowZoneCodeByName: Map<string, number>,
  zonesByCode: Map<number, Zone>
): SubBasinCsvFields {
  const col = (name: string): string => {
    const i = columnIndex.get(name);
    return i == null ? "" : (cols[i] ?? "");
  };
  const code = col("Codigo");
  const area = col("Superf");
  const aquiferRef = col("Cod_acuifero");
  const outletRef = col("Cod_aportacion");
  const precipCoef = col("Coef_precipitacion");
  const etpCoef = col("Coef_etp");
  const tempCoef = col("Coef_temperatura");
  const partialResults = col("Res_parciales");
  const zoneRef = col("Zona_parametros");
  const snowZoneRef = col("Zona_parametros_nieve");

  const outletCode = isUnassignedName(outletRef) ? 0 : (outletCodeByName.get(outletRef) ?? 0);
  const aquiferCode = isUnassignedName(aquiferRef) ? 0 : (aquiferCodeByName.get(aquiferRef) ?? 0);
  const zoneCode = isUnassignedName(zoneRef) ? null : (runoffZoneCodeByName.get(zoneRef) ?? null);
  const snowZoneCode = isUnassignedName(snowZoneRef)
    ? null
    : (snowZoneCodeByName.get(snowZoneRef) ?? null);

  // Same derivation rule as projectEntities.ts's own assignZoneInBulk: a
  // sub-basin's model always comes from whichever zone it points to.
  const modelTypeCode =
    (zoneCode != null ? zonesByCode.get(zoneCode)?.modelTypeCode : null) ?? NO_MODEL;
  const snowModelTypeCode =
    (snowZoneCode != null ? zonesByCode.get(snowZoneCode)?.snowModelTypeCode : null) ?? NO_MODEL;

  return {
    name: code,
    areaRaw: area,
    precipAdjustmentRaw: precipCoef,
    etpAdjustmentRaw: etpCoef,
    tempAdjustmentRaw: tempCoef,
    partialResultsRaw: partialResults,
    outletCode,
    outletUnresolvedName: outletCode === 0 && !isUnassignedName(outletRef) ? outletRef : null,
    aquiferCode,
    aquiferUnresolvedName: aquiferCode === 0 && !isUnassignedName(aquiferRef) ? aquiferRef : null,
    zoneCode,
    zoneUnresolvedName: zoneCode == null && !isUnassignedName(zoneRef) ? zoneRef : null,
    snowZoneCode,
    snowZoneUnresolvedName:
      snowZoneCode == null && !isUnassignedName(snowZoneRef) ? snowZoneRef : null,
    modelTypeCode,
    snowModelTypeCode,
  };
}

export function parseConfigValues(text: string): Map<string, string> {
  return new Map(parseSimpleCsv(text).map((cols) => [cols[0], cols[1]]));
}

export function unitOptionsFromConfigValues(
  values: Map<string, string>,
  fallback: UnitOption[]
): UnitOption[] {
  const unidades = Number(values.get("Unidades"));
  return Number.isFinite(unidades) ? unitOptionsFromCoefficient(unidades) : fallback;
}

interface AquiferOptionFlags {
  negatives: boolean | undefined;
  pumping: boolean | undefined;
}

/** Ent_Config.csv's two aquifer checkboxes — `undefined` means the key wasn't in the file at all (as opposed to present-but-unchecked). */
export function parseAquiferOptionFlags(values: Map<string, string>): AquiferOptionFlags {
  return {
    negatives: values.has("Op_neg_acu") ? values.get("Op_neg_acu") === "1" : undefined,
    pumping: values.has("Op_bombeos") ? values.get("Op_bombeos") === "1" : undefined,
  };
}
